import { FileDown } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { EnvironmentalReport } from '@siliconx/shared';
import { apiDownload } from '../../lib/api';

/**
 * SIL-477 — Reports tab. SIL-459 register R5: statuses are Draft / Final
 * with a real "data as of" timestamp — never "Filed" or "SE Verified". The
 * file itself is streamed fresh on every download (never a pre-generated
 * artifact), so `asOfIso` is labeled "Data as of", never "Generated on" —
 * that would imply a snapshot that doesn't exist.
 */
export function AvailableReportsCard({ reports }: { reports: EnvironmentalReport[] | undefined }) {
  return (
    <Card padding="md" className="flex flex-col gap-3">
      <SectionLabel>Available reports</SectionLabel>
      <div className="flex flex-col divide-y divide-zinc-100 rounded-md border border-zinc-200">
        {(reports ?? []).map((report) => (
          <div key={report.key} className="flex items-center gap-3 p-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-sm font-semibold text-zinc-800">{report.name}</span>
              <span className="font-mono text-[13px] text-zinc-500">
                {report.format}
                {report.asOfIso && ` · Data as of ${new Date(report.asOfIso).toISOString().slice(0, 10)}`}
              </span>
            </div>
            <StatusPill tone={report.status === 'final' ? 'green' : 'amber'}>
              {report.status === 'final' ? 'Final' : 'Draft'}
            </StatusPill>
            <button
              type="button"
              onClick={() => void apiDownload(report.downloadPath, `${report.key}.${report.format.toLowerCase()}`)}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
              aria-label={`Download ${report.name}`}
            >
              <FileDown className="size-3.5" />
              Download
            </button>
          </div>
        ))}
        {(reports ?? []).length === 0 && (
          <div className="p-3 text-xs text-zinc-500">No reports available yet.</div>
        )}
      </div>
    </Card>
  );
}
